import React, { useEffect, useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { ButtonWithSpinner } from '@eika/button-with-spinner';

import { useConfig } from '../../defaultConfig';
import { paymentFromSelector, useAccounts } from '../../fetchHooks/useAccount';
import useEFakturaAccountNumber, {
    useMutateEFakturaAccount,
    useRefreshEFakturaAccount,
} from '../../fetchHooks/useEFakturaAccount';
import useTranslation from '../../hooks/useTranslation';
import { EikaModal } from '../EikaModal';
import EFakturaAccountModalBody from './EFakturaAccountModalBody';
import { ACCOUNT_NOT_SET } from './constants';

import './EFakturaAccountModal.scss';

interface Props {
    open: boolean;
    setOpen: React.Dispatch<boolean>;
}

const EFakturaAccountModal: React.FC<Props> = ({ open, setOpen }) => {
    const {
        featureToggles: { enableEFakturaAccount },
    } = useConfig();
    const translated = useTranslation();
    const selectedAccountState = useState<string>(ACCOUNT_NOT_SET);
    const [selectedAccount, setSelectedAccount] = selectedAccountState;

    const { data: accounts = [], ...accountState } = useAccounts({ select: paymentFromSelector });
    const { data: eFakturaAccount, isLoading } = useEFakturaAccountNumber({ enabled: enableEFakturaAccount });
    const saveAccount = useMutateEFakturaAccount();
    const refreshEFakturaAccount = useRefreshEFakturaAccount();

    useEffect(() => {
        setSelectedAccount(eFakturaAccount?.value || ACCOUNT_NOT_SET);
    }, [setSelectedAccount, eFakturaAccount]);

    const resetQuery = () => {
        saveAccount.reset();
    };
    const saveEFakturaAccount = () => {
        if (selectedAccount) {
            saveAccount
                .mutateAsync(selectedAccount !== ACCOUNT_NOT_SET ? { value: selectedAccount } : undefined)
                .then(() => {
                    refreshEFakturaAccount();
                })
                .catch(() => {
                    // noop
                });
        }
    };

    const hasChanged = selectedAccount !== eFakturaAccount?.value;

    return (
        <EikaModal
            id="efaktura-account"
            contentShouldStretch
            open={open}
            setOpen={setOpen}
            onClose={resetQuery}
            timeOut={saveAccount.isSuccess ? 2500 : undefined}
            footerContent={
                hasChanged && !saveAccount.isSuccess && !saveAccount.isError && accounts.length > 0 ? (
                    <div className="efaktura-account__footer">
                        <ButtonWithSpinner
                            size="small"
                            buttonType="positive"
                            onClick={saveEFakturaAccount}
                            showSpinner={saveAccount.isPending}
                        >
                            {translated.save}
                        </ButtonWithSpinner>
                    </div>
                ) : undefined
            }
        >
            <EFakturaAccountModalBody
                isLoading={accountState.isLoading || isLoading}
                isSuccess={saveAccount.isSuccess}
                error={saveAccount.error as RequestExceptionWithResponse | undefined}
                submitOrRetry={saveEFakturaAccount}
                accounts={accounts}
                selectedAccountState={selectedAccountState}
            />
        </EikaModal>
    );
};

export default EFakturaAccountModal;
